import { useI18n } from '../i18n';
import React, { useEffect, useState } from 'react';
import { Download, RefreshCw, Share, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { PWA_UPDATE_READY_EVENT } from '../pwa/registerPwa';
import { isIosPwaDevice, isMobilePwaDevice } from '../utils/pwaDevice';
import { dismissPwaInstallPrompt, isPwaInstallPromptDismissed } from '../utils/pwaInstallPreference';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches
  || ('standalone' in navigator && (navigator as Navigator & { standalone?: boolean }).standalone === true);

const isOperationalMatchPath = (pathname: string) => [
  '/match/live',
  '/match-lineup',
  '/match-team-schedule',
].some(path => pathname.startsWith(path));

const isAuthenticationPath = (pathname: string) =>
  /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:login|register|auth\/verify)(?:\/|$)/i.test(pathname);

export const PwaInstallPrompt: React.FC = () => {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone() || !isMobilePwaDevice(navigator)) return;
    const isIos = isIosPwaDevice(navigator);
    setIos(isIos);
    if (isPwaInstallPromptDismissed(localStorage)) return;

    const onInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallEvent(event as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onInstallPrompt);
    const timer = isIos ? window.setTimeout(() => setVisible(true), 3000) : undefined;
    return () => {
      window.removeEventListener('beforeinstallprompt', onInstallPrompt);
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    dismissPwaInstallPrompt(localStorage);
    setVisible(false);
  };

  const install = async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice.outcome === 'accepted') setVisible(false);
    setInstallEvent(null);
  };

  if (!visible || isAuthenticationPath(pathname) || isOperationalMatchPath(pathname) || (!ios && !installEvent)) return null;

  return (
    <aside className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] left-3 right-3 z-[60] mx-auto max-w-md rounded-xl border border-gray-200 border-l-4 border-l-orange-500 bg-white px-3 py-3 shadow-lg" aria-label={t('pwa_install_label')}>
      <button type="button" onClick={dismiss} className="absolute right-1.5 top-1.5 inline-flex min-h-11 min-w-11 items-center justify-center text-gray-400 hover:text-gray-700" aria-label={t('pwa_install_dismiss')}><X size={21} /></button>
      <div className="flex items-start gap-3 pr-8">
        <img src="/app-icon-192.png" alt="" className="h-9 w-9 shrink-0 rounded-lg" />
        <div className="min-w-0 flex-1">
          <h2 className="pr-2 text-sm font-bold leading-5 text-gray-950">{t('pwa_install_title')}</h2>
          {ios ? (
            <ol className="mt-1 text-sm leading-5 text-gray-600">
              <li className="flex gap-2">
                <span className="font-bold text-primary-600">1.</span>
                <span>{t('pwa_install_ios_share')} <Share size={16} className="ml-1 inline -translate-y-px" aria-hidden="true" /></span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-primary-600">2.</span>
                <span>{t('pwa_install_ios_choose')} <strong className="font-bold text-gray-950">{t('pwa_install_ios_home')}</strong></span>
              </li>
            </ol>
          ) : (
            <button type="button" onClick={() => void install()} className="mt-3 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-gray-950 px-4 text-sm font-bold text-white"><Download size={17} /> {t('pwa_install_button')}</button>
          )}
        </div>
      </div>
    </aside>
  );
};

export const PwaUpdatePrompt: React.FC = () => {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    const onUpdateReady = (event: Event) => {
      setRegistration((event as CustomEvent<ServiceWorkerRegistration>).detail);
    };
    window.addEventListener(PWA_UPDATE_READY_EVENT, onUpdateReady);
    return () => window.removeEventListener(PWA_UPDATE_READY_EVENT, onUpdateReady);
  }, []);

  const activateUpdate = () => {
    if (!registration?.waiting) return;
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    }, { once: true });
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  };

  if (!registration) return null;

  return <PwaUpdateNotice onUpdate={activateUpdate} />;
};

export const PwaUpdateNotice: React.FC<{ onUpdate: () => void }> = ({ onUpdate }) => {
  const { t } = useI18n();
  return (
    <aside className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] left-3 right-3 z-[70] mx-auto max-w-lg rounded-md border border-gray-200 border-l-4 border-l-primary-500 bg-white px-3 py-3 shadow-lg min-[900px]:bottom-5" aria-live="polite">
      <div className="flex items-center gap-3">
        <span className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-50 text-primary-600 sm:inline-flex" aria-hidden="true"><RefreshCw size={17} /></span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold leading-5 text-gray-950">{t('pwa_update_title')}</p>
          <p className="text-xs leading-5 text-gray-600">{t('pwa_update_description')}</p>
        </div>
        <button type="button" onClick={onUpdate} className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-md bg-primary-600 px-3 text-sm font-bold text-white hover:bg-primary-700"><RefreshCw size={16} className="sm:hidden" /> {t('pwa_update_button')}</button>
      </div>
    </aside>
  );
};
